import { Box, Chip, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Button, ContainerPage, OptionsMenu, TitlePage } from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import purchaseSupplyConfigService from "services/purchaseSupplyService";
import { Dialog, Table } from "shared";

import { FormDrawer, WEEK_DAYS } from "./components/FormDrawer";
import { useStyles } from "./styles";

const PurchaseProductConfig = () => {
  const classes = useStyles();
  const { addPopup } = usePopup();
  const [drawerState, setDrawerState] = useState({
    open: false,
    mode: "create"
  });
  const [formData, setFormData] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, data: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, params, onChange, status, refresh, count, handleSearch } =
    useTableParams({
      service: queryParams => purchaseSupplyConfigService.getAll(queryParams),
      paramsDefault: {}
    });

  const handleEdit = item => {
    setFormData({
      id: item.id,
      productBase: {
        id: item.product_base_id,
        name: item.product_base_name
      },
      responsible: item.responsible_id
        ? { id: item.responsible_id, username: item.responsible_name }
        : null,
      arrival_days: item.arrival_days || [],
      min_order_quantity: item.min_order_quantity || 0
    });
    setDrawerState({ open: true, mode: "edit" });
  };

  const handleDelete = async () => {
    if (!deleteDialog?.data?.id) return;

    try {
      setIsSubmitting(true);
      await purchaseSupplyConfigService.remove(deleteDialog.data.id);
      addPopup({
        type: "success",
        title: "Configuração removida com sucesso."
      });
      setDeleteDialog({ open: false, data: null });
      refresh();
    } catch (error) {
      addPopup({
        type: "error",
        title: "Desculpe, ocorreu um erro",
        description: "Não foi possível remover a configuração."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const options = [
    {
      title: "Editar",
      icon: FiEdit2,
      permissions: ["Previsão", "Tech", "Admin"],
      action: handleEdit
    },
    {
      title: "Excluir",
      icon: MdOutlineDelete,
      color: "red",
      permissions: ["Previsão", "Tech", "Admin"],
      action: item => setDeleteDialog({ open: true, data: item })
    }
  ];

  const columns = [
    {
      field: "product_base_name",
      label: "Produto",
      disabledSort: true,
      style: { minWidth: "240px" }
    },
    {
      field: "responsible_name",
      label: "Responsável",
      disabledSort: true,
      style: { minWidth: "220px" },
      formatter: value => value || "--"
    },
    {
      field: "min_order_quantity",
      label: "Pedido Mínimo",
      disabledSort: true,
      align: "center",
      style: { minWidth: "140px" },
      formatter: value => {
        if (!value && value !== 0) return "--";
        return value.toString().replace(".", ",");
      }
    },
    {
      field: "arrival_days",
      label: "Dias de chegada",
      disabledSort: true,
      style: { minWidth: "260px" },
      renderCell: ({ data: item }) => {
        const arrivalDays = item.arrival_days || [];
        if (!arrivalDays.length) return <Typography>--</Typography>;

        // Filtra e ordena os dias que estão configurados
        const selectedDays = WEEK_DAYS.filter(day =>
          arrivalDays.includes(day.value)
        ).sort((a, b) => a.value - b.value);

        return (
          <Box display="flex" style={{ gap: 6, flexWrap: "wrap" }}>
            {selectedDays.map(day => (
              <Chip key={day.value} size="small" label={day.short} />
            ))}
          </Box>
        );
      }
    },
    {
      field: "updated",
      label: "Atualizado em",
      align: "center",
      disabledSort: true,
      style: { minWidth: "150px" },
      formatter: value =>
        value ? new Date(value).toLocaleDateString("pt-BR") : "--"
    },
    {
      field: "options",
      label: "",
      align: "right",
      renderCell: ({ data: item }) => (
        <OptionsMenu data={item} options={options} />
      )
    }
  ];

  return (
    <>
      <ContainerPage>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TitlePage>Configurações de Compra</TitlePage>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData(null);
              setDrawerState({ open: true, mode: "create" });
            }}
          >
            Adicionar produto
          </Button>
        </Box>

        <Table
          columns={columns}
          data={data}
          onChange={onChange}
          params={params}
          status={status}
          refresh={refresh}
          count={count}
          handleSearch={handleSearch}
          placeholder="Pesquisar produto ou responsável"
          hover
        />
      </ContainerPage>

      <FormDrawer
        open={drawerState.open}
        onClose={() => {
          setDrawerState({ open: false, mode: "create" });
          setFormData(null);
        }}
        initialData={formData}
        onSuccess={refresh}
      />

      <Dialog
        open={deleteDialog.open}
        handleClose={() => setDeleteDialog({ open: false, data: null })}
        title="Excluir configuração"
      >
        <Box>
          <Typography>
            Deseja excluir a configuração do produto{" "}
            <strong>{deleteDialog?.data?.product_base_name}</strong>?
          </Typography>

          <Box className={classes.footerButtons}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDeleteDialog({ open: false, data: null })}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Excluir
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default PurchaseProductConfig;
